import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';
import { useEffect, useRef } from 'react';
import { StoreMarkerContent } from '~/pages/LiveTracking/components/Markers/MarkerContents';
import {
  groupStoreLocations,
  isValidLatLngLiteral,
} from '~/pages/LiveTracking/utils';

type LocationInfo = {
  lng?: number;
  lat?: number;
};

type Props = {
  map: google.maps.Map | null;
  storeLocations: LocationInfo[];
};

export const LocationMarkerContent = () => {
  const container = document.createElement('div');
  container.style.cssText = `
    position: absolute;
    transform: translate(-50%, -100%);
    width: 32px;
    height: 32px;
  `;

  container.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" fill="none" style="width: 100%; height: 100%;">
        <path d="M39.7887 37.622L26 51.4107L12.2113 37.622C9.48424 34.8949 7.62707 31.4203 6.87468 27.6377C6.12229 23.855 6.50846 19.9343 7.98438 16.3711C9.4603 12.808 11.9597 9.7625 15.1664 7.61982C18.3732 5.47714 22.1433 4.3335 26 4.3335C29.8567 4.3335 33.6268 5.47714 36.8336 7.61982C40.0404 9.7625 42.5397 12.808 44.0156 16.3711C45.4915 19.9343 45.8777 23.855 45.1253 27.6377C44.3729 31.4203 42.5158 34.8949 39.7887 37.622V37.622ZM26 28.1667C27.1493 28.1667 28.2515 27.7101 29.0641 26.8975C29.8768 26.0848 30.3333 24.9826 30.3333 23.8333C30.3333 22.6841 29.8768 21.5819 29.0641 20.7692C28.2515 19.9565 27.1493 19.5 26 19.5C24.8507 19.5 23.7485 19.9565 22.9359 20.7692C22.1232 21.5819 21.6667 22.6841 21.6667 23.8333C21.6667 24.9826 22.1232 26.0848 22.9359 26.8975C23.7485 27.7101 24.8507 28.1667 26 28.1667Z" fill="#F24822"/>
    </svg>
  `;

  return container;
};

const useAddStoreMarker = ({ map, storeLocations }: Props) => {
  const listMarkerRef = useRef<
    {
      marker: google.maps.marker.AdvancedMarkerElement;
      id: string;
      locations: LocationInfo[];
    }[]
  >([]);

  const markerClusterRef = useRef<MarkerClusterer>();

  const addMarkers = (locations: LocationInfo[]) => {
    if (!locations.length) return;
    const { lat, lng } = locations[0];
    if (!isValidLatLngLiteral({ lat, lng })) return;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: {
        lat,
        lng,
      },
      content: LocationMarkerContent(),
    });

    listMarkerRef.current?.push({
      id: `${lat}-${lng}`,
      marker,
      locations,
    });

    markerClusterRef.current.addMarker(marker);
  };

  useEffect(() => {
    if (map) {
      markerClusterRef.current = new MarkerClusterer({
        map,
        renderer: {
          render({ count, position }) {
            const content = StoreMarkerContent({ clustersCount: count });

            return new google.maps.marker.AdvancedMarkerElement({
              content,
              position,
              // adjust zIndex to be above other markers
              zIndex: count,
            });
          },
        },
        onClusterClick: (
          event: google.maps.MapMouseEvent,
          cluster: Cluster,
          m: google.maps.Map,
        ) => {
          m.fitBounds(cluster.bounds);
        },
      });
    }
  }, [map]);

  useEffect(() => {
    if (storeLocations.length === 0 || !map) return;
    markerClusterRef.current.clearMarkers();
    const locationGrouped = groupStoreLocations(storeLocations as any);
    Object.keys(locationGrouped).forEach((key) => {
      addMarkers(locationGrouped[key]);
    });
  }, [map, storeLocations]);
};

export default useAddStoreMarker;
